import React, { useState, useMemo, useRef, useEffect } from "react";
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import InputGroup from 'react-bootstrap/InputGroup';
import 'ag-grid-enterprise';
import ConfirmDeleteModal from "../confirmdeletemodal";
import loaderGif from "../../../assets/loader.gif";
import api from '../../../config/axiosConfig';


const PublicHolidays = () => {
    const [formData, setFormData] = useState({
        id: null,  // Track if it's a new entry or an edit
        name: '',
        date: '',
        day: '',
        notes: '',
        isactive: true,
        createddate: '',
        modifieddate: ''
    });

    const [rowData, setRowData] = useState([]);
    const [isEditMode, setIsEditMode] = useState(false); // To track if we are in edit mode
    const [showModal, setShowModal] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [loader, setLoader] = useState(false);

    const getData = async () => {
        try {
            const response = await api.get('/publicholidays');
            setRowData(response.data);
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    };

    useEffect(() => {
        getData();
        //Generate day automatically from date
        if (formData.date) {
            const dayName = new Date(formData.date).toLocaleDateString('en-US', { weekday: 'long' });
            setFormData(prev => ({ ...prev, day: dayName }));
        }
    }, [formData.date]);
    
    const gridRef = useRef();

    // Column definitions for Ag-Grid
    const columnDefs = [

        { headerName: 'ID', field: 'id', sort: "desc", filter: 'agNumberColumnFilter' },
        { headerName: 'Name', field: 'name', filter: 'agTextColumnFilter' },
        {
            headerName: 'Date', field: 'date', filter: 'agDateColumnFilter',
            valueFormatter: (params) => {
                if (!params.value) return null;
                const date = new Date(params.value);
                return date.toLocaleDateString('en-CA'); // Format the date
            },
        },
        { headerName: 'Day', field: 'day', filter: 'agTextColumnFilter' },
        { headerName: 'Notes', field: 'notes', filter: 'agTextColumnFilter' },
        { headerName: 'Is Active', field: 'isactive'},
        { headerName: 'Created By', field: 'createdby', filter: 'agTextColumnFilter' },
        { headerName: 'Created At', field: 'createddate', filter: 'agDateColumnFilter' },
        { headerName: 'Updated By', field: 'modifiedby', filter: 'agTextColumnFilter' },
        { headerName: 'Updated At', field: 'modifieddate', filter: 'agDateColumnFilter' },
        {
            field: "actions",
            headerName: "Actions",
            cellRenderer: (params) => (
                <Button
                    variant="danger"
                    className="btn-sm"
                    onClick={() => handleDelete(params.data)}
                >
                    <i className="bi bi-trash3"></i>
                </Button>
            ),
        },
    ];

    const sideBar = useMemo(() => {
        return {
            toolPanels: [
                {
                    id: 'columns',
                    labelDefault: 'Columns',
                    labelKey: 'columns',
                    iconKey: 'columns',
                    toolPanel: 'agColumnsToolPanel',
                },
                {
                    id: 'filters',
                    labelDefault: 'Filters',
                    labelKey: 'filters',
                    iconKey: 'filter',
                    toolPanel: 'agFiltersToolPanel',
                }
            ],
            defaultToolPanel: '',
        };
    }, []);

    // Server-Side Datasource
    const datasource = {
        getRows: async function (params) {
            try {
                const { request } = params;

                const response = await api.post('/publicholidays/ag', { ...request });

                const { rows, lastRow } = response.data;

                // Send data back to AG Grid
                params.successCallback(rows, lastRow);
            } catch (error) {
                console.error("Error fetching rows:", error);
                params.failCallback();
            }
        }
    };

    const gridOptions = {
        rowModelType: 'serverSide',
        columnDefs: columnDefs,
        rowHeight: 60,

        defaultColDef: {
            sortable: true,
            filter: true,
            resizable: true,
            floatingFilter: true,
            enableValue: true,
            enableRowGroup: true,
            enablePivot: true,
            filterParams: {
                buttons: ["reset"],
                closeOnApply: true,
            },
        },

        sideBar: sideBar,
        rowGroupPanelShow: 'always',
        rowSelection: 'single',
        columnHoverHighlight: true,
        suppressRowGroupHidesColumns: true,
        cacheBlockSize: 20,
    }

    // Attach the datasource when the grid is ready
    const onGridReady = (params) => {
        params.api.setServerSideDatasource(datasource);
    };

    // Row selection handler
    const onRowSelected = (event) => {
        if (event.node.isSelected()) {
            const selectedRow = event.data;
            setFormData({
                id: selectedRow.id,
                name: selectedRow.name,
                date: selectedRow.date,
                day: selectedRow.day,
                notes: selectedRow.notes,
                isactive: selectedRow.isactive
            });

            setIsEditMode(true);  // Switch to edit mode
            setSelectedRow(selectedRow)
        }
    };

    // Handle form input changes
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    // Handle form submission
    // Handle form submission for adding/updating media
    const handleAddData = async (e) => {
        e.preventDefault();
        setLoader(true);
        // Prepare data for media entry
        const publicHolidaysData = {
            name: formData.name,
            date: formData.date || null,
            day: formData.day || null,
            notes: formData.notes || null,
            isactive: formData.isactive,
            createdat: new Date(),

        };
        // Determine whether to update or create a new entry
        const url = isEditMode ? `/publicholidays/${formData.id}` : '/publicholidays';
        const request = isEditMode ? api.put(url, publicHolidaysData) : api.post(url, publicHolidaysData);

        try {
            const response = await request;

            // Clear form fields and reset edit mode
            setFormData({ id: null, name: '', date: '', day: '', notes: '', isactive: true });
            setIsEditMode(false);
            setLoader(false);
            gridRef.current.api.refreshServerSideStore({ purge: true });

        } catch (error) {
            console.error("Error adding/updating public holidays:", error);
            setLoader(false);
        }
    };


    // Reset form for new entry
    const handleNewEntry = () => {
        setFormData({ id: null, name: '', date: '', day: '', notes: '', isactive: true });
        setIsEditMode(false);  // Switch to add mode
    };



    const handleDelete = () => {
        setShowModal(true);
    };

    const confirmDelete = async () => {
        try {
            await api.delete(`/publicholidays/${selectedRow.id}`);
            setShowModal(false);
            gridRef.current.api.refreshServerSideStore({ purge: true });
        } catch (error) {
            console.error('Error deleting public holidays:', error);
        }
    };

    return (
        <>
            <Container fluid>
                <Row className="mt-4">
                    <h6 className="text-start">Public Holidays List</h6>
                    <div className="ag-theme-alpine" style={{ height: 400, width: '100%' }}>
                        <AgGridReact
                            ref={gridRef}
                            gridOptions={gridOptions}
                            onGridReady={onGridReady}
                            onRowSelected={onRowSelected}
                        />
                    </div>
                </Row>
                <Row className="mt-4 border shadow p-4">
                    {loader && (
                        <div className="loader-overlay">
                            <img src={loaderGif} alt="Loading..." className="loader" />
                        </div>
                    )}
                    <Col md={12}>
                        <h6 className="text-start">{isEditMode ? "Edit Public Holiday" : "Add Public Holiday"}</h6>
                        <Form onSubmit={handleAddData}>
                            <Row>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label className="me-3">Name:</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="name"
                                            placeholder="Enter public holiday`s name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            required
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Check
                                            type="checkbox"
                                            name="isactive"
                                            label="Is Active"
                                            checked={formData.isactive}
                                            onChange={handleChange}
                                            className="ms-4"
                                        />
                                    </Form.Group>
                                </Col>
                            </Row><br />
                            <Row className="mb-4">
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Date</Form.Label>
                                        <InputGroup>
                                            <Form.Control
                                                type="date"
                                                name="date"
                                                value={formData.date}
                                                onChange={handleChange}
                                            />
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Day</Form.Label>
                                        <InputGroup>
                                            <Form.Control
                                                type="text"
                                                name="day"
                                                value={formData.day}
                                                onChange={handleChange}
                                            />
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row className="mb-4">
                                <Col md={12}>
                                    <Form.Group>
                                        <Form.Label>Note</Form.Label>
                                        <InputGroup>
                                            <Form.Control
                                                as="textarea" // Use 'as' to specify the HTML element
                                                rows={3}      // Optional: Number of rows for the textarea
                                                name="notes"
                                                placeholder="Enter Description"
                                                value={formData.notes}
                                                onChange={handleChange}
                                            />
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Button variant="primary" type="submit">
                                {isEditMode ? "Update" : "Add"}
                            </Button>
                            {isEditMode ?
                                <Button variant="secondary" className="ms-3" onClick={handleNewEntry}>
                                    Create New Entry
                                </Button>
                                : ''
                            }
                        </Form>
                    </Col>
                </Row>
            </Container>

            {/* Modal for confirming deletion */}
            <ConfirmDeleteModal
                show={showModal}
                handleClose={() => setShowModal(false)}
                handleConfirm={confirmDelete}
                title="Confirm Deletion"
                body={`Are you sure you want to delete ${selectedRow?.name}?`}
                confirmText="Delete"
            />




        </>
    );
};

export default PublicHolidays;